import { Injectable } from '@angular/core';
import { GeolocationStore } from '../stores/geolocation.store';
import { AnalyticsService } from './analytics.service';
import { RemoteLoggerService } from './remote-logger.service';
import { IGeolocationCoordinates } from '../models';
import { unawaited } from '../utility/unawaited';
import { hasKey } from 'utility/unknown.util';

@Injectable({
  providedIn: 'root'
})
export class NavigatorGeolocationService {
  constructor(
    private analyticsService: AnalyticsService,
    private geolocationStore: GeolocationStore,
    private logger: RemoteLoggerService
  ) {}

  private readonly geolocationErrorCodes = {
    'geolocation.error.unsupportedBrowser':
      'Browser does not support location services',
    'geolocation.error.permissionDenied':
      'You have rejected access to your location',
    'geolocation.error.positionUnavailable':
      'Unable to determine your location',
    'geolocation.error.timeout': 'Service timeout has been reached'
  };

  getCurrentPosition(
    geolocationOptions?: PositionOptions
  ): Promise<IGeolocationCoordinates> {
    geolocationOptions = geolocationOptions || {
      enableHighAccuracy: true,
      timeout: 10000
    };

    if (!(window.navigator && window.navigator.geolocation)) {
      this.analyticsService.log(
        this.geolocationErrorCodes['geolocation.error.unsupportedBrowser']
      );
    }

    return new Promise((resolve, reject) => {
      window.navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude: lat, longitude: long } = position.coords;
          resolve({ lat, long });
        },
        (error) => reject(error),
        geolocationOptions
      );
    });
  }

  async loadCurrentPosition(
    geolocationOptions?: PositionOptions
  ): Promise<void> {
    try {
      this.toggleLoadingGeolocation(true);
      this.geolocationStore.geolocationCoordinates =
        await this.getCurrentPosition(geolocationOptions);
      this.geolocationStore.permissionDenied = false;
    } catch (error) {
      this.logger.capture(error);
      this.geolocationStore.geolocationCoordinates = null;
      if (!hasKey(error, 'code')) {
        return;
      }
      switch (error.code) {
        case 1:
          this.analyticsService.log(
            this.geolocationErrorCodes['geolocation.error.permissionDenied']
          );
          this.geolocationStore.permissionDenied = true;

          break;
        case 2:
          this.analyticsService.log(
            this.geolocationErrorCodes['geolocation.error.positionUnavailable']
          );
          break;
        case 3:
          this.analyticsService.log(
            this.geolocationErrorCodes['geolocation.error.timeout']
          );
          break;
      }
    } finally {
      this.toggleLoadingGeolocation(false);
    }
  }

  async updateCurrentPosition() {
    const isGeolocationGranted = await this.isGeolocationGranted();

    if (!!isGeolocationGranted) {
      unawaited(this.loadCurrentPosition());
    } else {
      this.geolocationStore.permissionDenied = await this.isGeolocationDenied();
    }
  }

  async isGeolocationGranted(): Promise<boolean> {
    const currentPermissionState = await this.getCurrentPermissionState();
    return currentPermissionState === 'granted';
  }

  async isGeolocationDenied(): Promise<boolean> {
    const currentPermissionState = await this.getCurrentPermissionState();
    return currentPermissionState === 'denied';
  }

  async getCurrentPermissionState(): Promise<PermissionState | null> {
    if (navigator.permissions) {
      const permissionState = await navigator.permissions.query({
        name: 'geolocation'
      });

      return permissionState.state;
    }
    return null;
  }

  private toggleLoadingGeolocation(isLoading: boolean) {
    this.geolocationStore.isLoadingGeolocation = isLoading;
  }
}
